import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { DetailsItem } from '@src/orders/components/DetailsItem';
import type { ReviewableOrder } from '@src/orders/domain/ReviewableOrder';
import { useTranslation } from 'react-i18next';

export const ReviewableOrderDetails = ({
  order,
}: {
  order: ReviewableOrder;
}) => {
  const { t } = useTranslation();
  const clientDba = order.client.doingBusinessAs;
  const clientName = order.client.name;
  const invoicingInstructions = order.billTo.invoicingInstructions;
  return (
    <Stack className={summaryCard}>
      <Stack direction="row">
        <Stack className={element}>
          <Typography variant="subtitle1">{clientDba ?? clientName}</Typography>
          {!!clientDba && <Typography variant="body1">{clientName}</Typography>}
          {invoicingInstructions && (
            <DetailsItem
              itemName={t('reviewPermit.invoicingInstructions')}
              value={invoicingInstructions}
            />
          )}
        </Stack>

        <Stack className={element}>
          <DetailsItem
            itemName={t('reviewPermit.billingCurrency')}
            value={
              order.billingCurrency
                ? t(`currency.${order.billingCurrency.toLowerCase()}`)
                : undefined
            }
          />
          <DetailsItem itemName={t('agent')} value={order.claimedBy?.name} />
        </Stack>
      </Stack>

      <Divider
        style={{
          marginBottom: theme.spacing['4'],
          marginTop: theme.spacing['4'],
        }}
      />

      <Stack direction="row">
        <Stack className={element}>
          <DetailsItem
            itemName={t('orderSummary.tractorUnitNumber')}
            value={order.tractor.unit?.toString()}
          />
          <DetailsItem
            itemName={t('orderSummary.tractorLicensePlate')}
            value={order.tractor.licensePlate}
          />
          <DetailsItem
            itemName={t('orderSummary.driverName')}
            value={order.driver.name}
          />
        </Stack>

        <Stack className={element}>
          <DetailsItem
            itemName={t('orderSummary.purchaseOrder')}
            value={order.PONumber}
          />
          <DetailsItem
            itemName={t('orderSummary.loadSerialNumber')}
            value={order.loadInformation.serialNumber}
          />
          <DetailsItem
            itemName={t('orderSummary.submitted')}
            value={order.submittedDate.formatDate()}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const summaryCard = css({
  backgroundColor: theme.palette.surface.surface,
  borderRadius: '12px',
  padding: '24px',
});

const element = css({
  flex: 1,
  gap: theme.spacing['4'],
});
